/**
 * Configuração centralizada de roles do sistema
 * Single source of truth para todas as roles e suas propriedades
 */

export interface RoleConfig {
    name: string;
    label: string;
    description: string;
    icon: string;
    severity: 'danger' | 'warning' | 'info' | 'success' | 'secondary';
    color: string;
    route: string;
}

/**
 * Lista oficial de roles do sistema
 * IMPORTANTE: Manter sincronizado com RoleSeeder.php
 */
export const SYSTEM_ROLES = {
    ADMIN: 'admin',
    ALUNO: 'aluno',
    ORIENTADOR: 'orientador',
    COORDENADOR: 'coordenador',
    SECRETARIA: 'secretaria',
    COORDENADOR_TCC: 'coordenador_tcc',
    USER_NORMAL: 'user_normal',
} as const;
export const ROLES_CONFIG: Record<string, RoleConfig> = {
    [SYSTEM_ROLES.ADMIN]: {
        name: 'admin',
        label: 'Administrador',
        description: 'Administrador do Sistema',
        icon: 'pi-shield',
        severity: 'danger',
        color: '#dc3545',
        route: '/admin',
    },
    [SYSTEM_ROLES.COORDENADOR]: {
        name: 'coordenador',
        label: 'Coordenador',
        description: 'Coordenador de Curso',
        icon: 'pi-briefcase',
        severity: 'info',
        color: '#17a2b8',
        route: '/gerenciar-trabalhos',
    },
    [SYSTEM_ROLES.SECRETARIA]: {
        name: 'secretaria',
        label: 'Secretária',
        description: 'Secretária da Coordenação',
        icon: 'pi-id-card',
        severity: 'info',
        color: '#0ea5e9',
        route: '/gerenciar-trabalhos',
    },
    [SYSTEM_ROLES.COORDENADOR_TCC]: {
        name: 'coordenador_tcc',
        label: 'Coord. de TCC',
        description: 'Coordenador de TCC',
        icon: 'pi-clipboard',
        severity: 'info',
        color: '#6366f1',
        route: '/gerenciar-trabalhos',
    },
    [SYSTEM_ROLES.ORIENTADOR]: {
        name: 'orientador',
        label: 'Orientador',
        description: 'Orientador',
        icon: 'pi-user-edit',
        severity: 'warning',
        color: '#ffc107',
        route: '/painel-orientador',
    },
    [SYSTEM_ROLES.ALUNO]: {
        name: 'aluno',
        label: 'Aluno',
        description: 'Aluno',
        icon: 'pi-graduation-cap',
        severity: 'success',
        color: '#28a745',
        route: '/projetos',
    },
    [SYSTEM_ROLES.USER_NORMAL]: {
        name: 'user_normal',
        label: 'Usuário Normal',
        description: 'Usuário Normal',
        icon: 'pi-user',
        severity: 'secondary',
        color: '#6c757d',
        route: '/projetos',
    },
} as const;

export const ROLE_PRIORITY = [
    SYSTEM_ROLES.ADMIN,
    SYSTEM_ROLES.COORDENADOR,
    SYSTEM_ROLES.SECRETARIA,
    SYSTEM_ROLES.COORDENADOR_TCC,
    SYSTEM_ROLES.ORIENTADOR,
    SYSTEM_ROLES.ALUNO,
    SYSTEM_ROLES.USER_NORMAL,
] as const;

/**
 * Opções para dropdowns/selects
 */
export const ROLE_OPTIONS = [
    { label: ROLES_CONFIG[SYSTEM_ROLES.ADMIN].label, value: SYSTEM_ROLES.ADMIN },
    { label: ROLES_CONFIG[SYSTEM_ROLES.COORDENADOR].label, value: SYSTEM_ROLES.COORDENADOR },
    { label: ROLES_CONFIG[SYSTEM_ROLES.SECRETARIA].label, value: SYSTEM_ROLES.SECRETARIA },
    { label: ROLES_CONFIG[SYSTEM_ROLES.COORDENADOR_TCC].label, value: SYSTEM_ROLES.COORDENADOR_TCC },
    { label: ROLES_CONFIG[SYSTEM_ROLES.ORIENTADOR].label, value: SYSTEM_ROLES.ORIENTADOR },
    { label: ROLES_CONFIG[SYSTEM_ROLES.ALUNO].label, value: SYSTEM_ROLES.ALUNO },
    { label: ROLES_CONFIG[SYSTEM_ROLES.USER_NORMAL].label, value: SYSTEM_ROLES.USER_NORMAL },
] as const;

/**
 * Helpers para trabalhar com roles
 */
export const RoleUtils = {
    getConfig(roleName: string): RoleConfig | undefined {
        return ROLES_CONFIG[roleName];
    },

    getLabel(roleName: string): string {
        return ROLES_CONFIG[roleName]?.label || roleName;
    },

    getSeverity(roleName: string): 'danger' | 'warning' | 'info' | 'success' | 'secondary' {
        return ROLES_CONFIG[roleName]?.severity || 'secondary';
    },

    getIcon(roleName: string): string {
        return ROLES_CONFIG[roleName]?.icon || 'pi-user';
    },

    getRoute(roleName: string): string {
        return ROLES_CONFIG[roleName]?.route || '/dashboard';
    },

    getDescription(roleName: string): string {
        return ROLES_CONFIG[roleName]?.description || '';
    },

    formatRoles(roles: string[]): string {
        return roles.map(role => this.getLabel(role)).join(', ');
    },

    sortByPriority(roles: string[]): string[] {
        return [...roles].sort((a, b) => {
            const indexA = ROLE_PRIORITY.indexOf(a as any);
            const indexB = ROLE_PRIORITY.indexOf(b as any);

            if (indexA === -1 && indexB === -1) return a.localeCompare(b);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;

            return indexA - indexB;
        });
    },

    getDefaultRole(userRoles: string[]): string {
        for (const role of ROLE_PRIORITY) {
            if (userRoles.includes(role)) {
                return role;
            }
        }
        return userRoles[0] || SYSTEM_ROLES.USER_NORMAL;
    },
};
